import { defineStore } from 'pinia';
import { computed, onMounted, ref, watch } from 'vue';
import type { WordOfTheDayItem } from '@ilteducation/word-of-the-day-types';
import { useConfigHelper } from '@ilteducation/config';
import axios from 'axios';
import { useGameEvents } from '@ilteducation/activity-stream-events';
import { v4 as uuidv4 } from 'uuid';
import { useMyOrganizations } from '@ilteducation/my-account';
import type { SlimOrganization } from '@ilteducation/my-account-types';
import { useUserContext } from '@ilteducation/ilt-auth';

export type WordContext = { description?: string; imageUrl?: string };

const useWordOfTheDayStore = defineStore('games-word-of-the-day', () => {
  const gameState = ref<'init' | 'game' | 'game-over' | 'results'>('init');
  const keywordOfTheDay = ref<WordOfTheDayItem>();
  const word = ref('');
  const selectedLetters = ref<Set<string>>(new Set());
  const correctLetters = ref<string[]>([]);
  const wordContext = ref<WordContext>();
  const guesses = computed(() => selectedLetters.value.size - correctLetters.value.length);
  const guessedCorrectWord = computed(() => correctLetters.value.length === new Set(word.value.split('')).size);
  const { getApiUrl } = useConfigHelper();
  const cdnDomain = process.env.VITE_CDN_DOMAIN;
  const sessionId = ref<string>(uuidv4()); // Generate a random session ID
  const { user, roles } = useUserContext();
  const { gameStarted, gameEnded } = useGameEvents({ game: 'word-of-the-day' });
  const { organizations } = useMyOrganizations(true);

  const sendGameStartEvent = () => {
    gameStarted(
      sessionId.value,
      user.uid,
      roles.map((r) => r.value),
      organizations.value.data.map((org: SlimOrganization) => org.uid),
    );
  };

  const sendGameEndEvent = () => {
    gameEnded({
      game: 'word-of-the-day',
      incorrectGuesses: guesses.value,
      win: guessedCorrectWord.value,
    });
  };

  const getKeywordOfTheDay = async (): Promise<WordOfTheDayItem> => {
    const kw = await axios.get(`${getApiUrl('word-of-the-day', true)}/word`);
    return kw.data;
  };

  const setGameState = (state: 'init' | 'game' | 'results' | 'game-over') => {
    gameState.value = state;
  };

  const getWordProgress = computed(() => {
    return word.value
      .split('')
      .map((letter) => (selectedLetters.value.has(letter) ? letter : ' '))
      .join('');
  });

  const makeGuess = (letter: string) => {
    // eslint-disable-next-line no-param-reassign
    letter = letter.toLowerCase();
    selectedLetters.value.add(letter);
    if (word.value.includes(letter)) {
      if (!correctLetters.value.includes(letter)) {
        correctLetters.value.push(letter);
      }
    }
    if (guesses.value === 10 || correctLetters.value.length === new Set(word.value.split('')).size) {
      setGameState('game-over'); // End game if 10 guesses are made
    }
  };
  const clear = () => {
    gameState.value = 'init';
    selectedLetters.value = new Set();
    correctLetters.value = [];
  };

  onMounted(async () => {
    keywordOfTheDay.value = await getKeywordOfTheDay();
    wordContext.value = {
      description: keywordOfTheDay.value.description,
      imageUrl: keywordOfTheDay.value.imageId
        ? `${cdnDomain}/images/${keywordOfTheDay.value.imageId}/image.jpeg`
        : undefined,
    };
    word.value = keywordOfTheDay.value.keyword;
  });

  watch(gameState, (gs) => {
    if (gs === 'game-over') {
      sendGameEndEvent();
      sessionId.value = uuidv4(); // Reset session ID for the next game
    }
  });

  return {
    gameState,
    word,
    wordContext,
    selectedLetters,
    setGameState,
    getWordProgress,
    makeGuess,
    clear,
    guesses,
    guessedCorrectWord,
    keywordOfTheDay,
    sendGameStartEvent,
    sendGameEndEvent,
  };
});

export default useWordOfTheDayStore;
